const ItemType = require('@/lib/Items/ItemType');
const LocalStorageKeys = require('@/lib/Storage/LocalStorageKeys');
const OptionSetConfigError = require('@/lib/ItemOptions/OptionSetConfigError');
const pluralS = require('@/filters/pluralS');
const { container } = require('@/container');
const { md5Hash } = require('@/functions/hash');

/**
 * This is used to check the setup of
 */
class ItemOptionConfigValidator {
    /**
     * @param {DialogService} dialogService
     * @param {OptionSetRepository} optionSetRepository
     * @param {ItemOptionManager} itemOptionManager
     * @param {ItemRepository} itemRepository
     * @param {LocalStorageDataStore} localStorageDataStore
     */
    constructor(
        dialogService,
        optionSetRepository,
        itemOptionManager,
        itemRepository,
        localStorageDataStore,
    ) {
        this.dialogService = dialogService;
        this.optionSetRepository = optionSetRepository;
        this.itemOptionManager = itemOptionManager;
        this.itemRepository = itemRepository;
        this.localStorageDataStore = localStorageDataStore;
    }

    getCurrentItemBranchID() {
        return this.itemOptionManager.userService.currentItemBranch.ID;
    }

    /**
     * Validate all active option sets and display any errors to the user.
     *
     * We track which errors have already been shown using a hash stored in local storage to prevent repeatedly
     * showing the same errors to the user. This is tracked per-branch and is cleared when the device is synced.
     * The hash is based on the error messages so if the errors change the user will see the new errors.
     */
    validateAndDisplayErrors() {
        const currentItemBranchID = this.getCurrentItemBranchID();

        const localStorageKey = LocalStorageKeys.LAST_ITEM_OPTIONS_ERROR_HASH + '.' + currentItemBranchID;

        // Check the last-shown validation error hash.
        const lastShownErrorHash = this.localStorageDataStore.find(localStorageKey);

        this.validateAllActiveOptionSets()
            /**
             * @param {OptionSetConfigError[]} errors
             */
            .then((errors) => {
                if (errors.length > 0) {
                    console.warn('Item option configuration errors found:', errors);

                    // Build a nice HTML string to display the messages to the user.
                    let html = container.langService.getText('ITEM_OPTIONS.CONFIG_ERROR_INTRO');
                    html += errors.map((err) => {
                        return `<div class="dialog-message-standout"><i class="fa-solid fa-exclamation-circle"></i> <em>${err.optionSet.name}</em> ${err.message}</div>`;
                    }).join('');
                    html += `<p><small>${container.langService.getText('ITEM_OPTIONS.CONFIG_ERROR_INFO')}</small></p>`;

                    // Create a hash of the error message.
                    const errorMessageHash = md5Hash(html);
                    // If this is the same as the last shown error hash, don't show it again.
                    if (lastShownErrorHash && errorMessageHash === lastShownErrorHash) {
                        console.warn('Item option configuration errors already shown to user, skipping display.');
                        return;
                    }
                    // Store the new error hash.
                    this.localStorageDataStore.persist(localStorageKey, errorMessageHash);

                    // Display the errors to the user.
                    this.dialogService.alert(
                        null,
                        {
                            color: 'warning',
                            icon: 'fa-exclamation-triangle',
                            title: 'ITEM_OPTIONS.CONFIG_ERROR_TITLE',
                            customHtml: html,
                        }
                    );
                } else {
                    console.log('✅ Items options passed validation with no errors.');
                    // Clear any stored error hash as there are no errors now.
                    this.localStorageDataStore.remove(localStorageKey);
                }
            });
    }

    /**
     * @returns {Promise<{optionSet: OptionSet, triggeredByItem: AbstractItem}[]>}
     */
    async getAllApplicableOptionSets() {
        // Load all the active option sets in storage.
        const optionSets = await this.optionSetRepository.findAllActive();

        const currentItemBranchID = this.getCurrentItemBranchID();

        // TODO: We will be adding branch restrictions to option sets in future (PROP-2237) but for now
        // we will filter to only option sets that have any items available in the current branch.
        const filteredOptionSets = [];
        await Promise.all(
            optionSets.map(async (optionSet) => {
                for (let i = 0; i < optionSet.itemIDs.length; i++) {
                    // TODO: The item repository needs to know the item type to load an item.
                    // We know at present option sets only work with products so we can hardcode that for now.
                    // But when it is expanded we'll need to change this.
                    try {
                        const item = await this.itemRepository.getItem(ItemType.PRODUCT, optionSet.itemIDs[i]);
                        if (item.isActive() && item.isForBranch(currentItemBranchID)) {
                            filteredOptionSets.push({
                                optionSet,
                                triggeredByItem: item,
                            });
                            return;
                        }
                    } catch (e) {
                        // Item not found, so skip it.
                    }
                }
                // No items found for this option set in the current branch, so skip it.
            })
        );

        return filteredOptionSets;
    }

    /**
     * Validate all active option sets in the system.
     * Returns an array of errors found. This will be empty if no errors found.
     *
     * @returns {Promise<Error[]>}
     */
    async validateAllActiveOptionSets() {
        const optionSets = await this.getAllApplicableOptionSets();

        const errors = [];
        for (const optionSet of optionSets) {
            try {
                await this.validateOptionSet(optionSet.optionSet, optionSet.triggeredByItem);
            } catch (err) {
                errors.push(err);
            }
        }

        return errors;
    }

    /**
     * @param {OptionSet} optionSet
     * @param {AbstractItem} [triggeredByItem] Used in the error message to indicate an example item that will
     *                                         trigger this option set.
     *
     * @throws {Error} Throws an error if the option set is invalid.
     */
    async validateOptionSet(optionSet, triggeredByItem = null) {
        // If the option set has a minimum number of options check that it's possible to satisfy that.

        if (optionSet.minOptions < 1) {
            // No minimum, so always valid.
            return;
        }

        // Load all the options for the set.
        // This will return only active options that are selectable for the current branch.
        await this.itemOptionManager.attachOptionsToSet(optionSet);

        // Ensure there are enough active options to satisfy the minimum.
        if (!optionSet.options || optionSet.options.length < optionSet.minOptions) {
            const availableCount = optionSet.options ? optionSet.options.length : 0;

            // The message wil be prepended with the option set name when show in the dialog.
            let errorMessage = `requires at least ${optionSet.minOptions} option${pluralS(optionSet.minOptions)}, `;

            if (availableCount === 0) {
                errorMessage += 'but has no options available in the current branch.';
            } else {
                errorMessage += `but only has ${availableCount} option${pluralS(availableCount)} available in the current branch.`;
            }

            throw new OptionSetConfigError(errorMessage, optionSet, triggeredByItem);
        }
    }
}

module.exports = ItemOptionConfigValidator;
